import { useCallback, useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "../lib/api";

/** Fill in the author and licence for any image whose record is incomplete. */
export default function Licences() {
  const [d, setD] = useState(null);
  const [edits, setEdits] = useState({});
  const [busy, setBusy] = useState("");
  const [onlyGaps, setOnlyGaps] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/licences");
      setD(data);
      setEdits({});
    } catch (err) {
      toast.error(errText(err));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!d) return <p className="text-zinc-600" data-testid="admin-licences-loading">Loading…</p>;

  const rows = [...d.portraits, ...d.assets].filter((r) => (onlyGaps ? !r.complete : true));
  const edit = (id, key, value) =>
    setEdits((prev) => ({ ...prev, [id]: { ...prev[id], [key]: value } }));

  const save = async (r) => {
    const e = edits[r.id] || {};
    setBusy(r.id);
    try {
      await api.put("/admin/asset-licences", {
        asset_id: r.id,
        author: e.author ?? r.author ?? "",
        licence: e.licence ?? r.licence ?? "",
        source_url: e.source_url ?? r.source_url ?? "",
        permission_note: e.permission_note ?? r.permission_note ?? "",
        checked_on: e.checked_on ?? r.checked_on ?? "",
      });
      toast.success("Recorded");
      load();
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy("");
    }
  };

  const clear = async (r) => {
    setBusy(r.id);
    try {
      await api.delete(`/admin/asset-licences/${r.id}`);
      toast.success("Record cleared — this image is a gap again");
      load();
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy("");
    }
  };

  return (
    <div data-testid="admin-licences">
      <p className="overline">Transparency</p>
      <h1 className="display-2 mt-4">Image licence register</h1>
      <p className="mt-4 max-w-3xl text-sm leading-relaxed text-zinc-400">
        A Creative Commons photograph needs the author, the licence and a link to the original file — a credit
        naming only the website is not enough. Open the file page on Wikimedia Commons, copy the author and the
        licence, and record them here. Anything left incomplete is published as a gap on the public register.
      </p>

      <div className="mt-8 flex flex-wrap items-center gap-4">
        <button type="button" onClick={() => setOnlyGaps((v) => !v)} data-testid="licences-toggle-gaps"
          className={`mono border px-4 py-2 text-[0.55rem] uppercase tracking-[0.16em] ${onlyGaps ? "border-ochre bg-ochre text-black" : "border-white/15 text-zinc-400"}`}>
          {onlyGaps ? "Showing incomplete only" : "Showing everything"}
        </button>
        <p className="mono text-[0.55rem] uppercase tracking-[0.14em] text-zinc-500" data-testid="licences-counts">
          {d.totals.recorded} of {d.totals.items} recorded · {d.totals.gaps} incomplete
        </p>
      </div>

      <div className="mt-8 space-y-4">
        {rows.map((r) => {
          const e = edits[r.id] || {};
          return (
            <div key={r.id} className="border border-zinc-900 bg-ink-surface p-5" data-testid={`admin-licence-${r.id.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-white">{r.description}</p>
                <span className={`mono text-[0.55rem] uppercase tracking-[0.14em] ${r.complete ? "text-ochre" : "text-maasai"}`}>
                  {r.complete ? "Complete" : "Incomplete"}
                </span>
              </div>
              <p className="mono mt-1 text-[0.55rem] uppercase tracking-[0.14em] text-zinc-500">
                {(r.paths?.length ? r.paths : [r.path]).filter(Boolean).join(" · ")}
              </p>
              {r.published_credit ? (
                <p className="mt-2 text-xs text-zinc-500">Credit shown on the site now: {r.published_credit}</p>
              ) : null}
              <div className="mt-4 grid gap-3 md:grid-cols-[1.4fr_1.1fr_1.4fr_0.8fr_auto_auto]">
                <input className="field" placeholder="Author, as named on the file page"
                  value={e.author ?? r.author ?? ""} onChange={(ev) => edit(r.id, "author", ev.target.value)}
                  data-testid={`licence-author-${r.id.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`} />
                <select className="field bg-black" value={e.licence ?? r.licence ?? ""}
                  onChange={(ev) => edit(r.id, "licence", ev.target.value)}
                  data-testid={`licence-select-${r.id.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                  <option value="">Licence not recorded</option>
                  {d.licences.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
                <input className="field" placeholder="Link to the file page (not the article)"
                  value={e.source_url ?? r.source_url ?? ""} onChange={(ev) => edit(r.id, "source_url", ev.target.value)}
                  data-testid={`licence-source-${r.id.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`} />
                <input className="field" placeholder="Checked (e.g. Jun 2026)"
                  value={e.checked_on ?? r.checked_on ?? ""} onChange={(ev) => edit(r.id, "checked_on", ev.target.value)}
                  data-testid={`licence-checked-${r.id.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`} />
                <button type="button" onClick={() => save(r)} disabled={busy === r.id}
                  className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
                  data-testid={`licence-save-${r.id.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                  {busy === r.id ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save
                </button>
                <button type="button" onClick={() => clear(r)} disabled={busy === r.id}
                  className="mono border border-maasai/60 px-4 text-[0.55rem] uppercase tracking-[0.14em] text-maasai disabled:opacity-50"
                  data-testid={`licence-clear-${r.id.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                  Clear
                </button>
              </div>
            </div>
          );
        })}
        {!rows.length ? (
          <p className="text-sm text-zinc-500" data-testid="admin-licences-empty">
            Every image has a complete record. Nothing to fill in.
          </p>
        ) : null}
      </div>
    </div>
  );
}
